import { useEffect, useRef } from "react";
import {
  PlusIcon,
  SendIcon,
  MessageSquareIcon,
  X,
  Pencil,
  Check,
} from "lucide-react";
import { ChatMessage } from "./chat-message";
import { PromptLibrary } from "./prompt-library";
import { TypingIndicator } from "./typing-indicator";
import { useAssistantChat } from "../hooks";
import { usePostHog } from "@posthog/react";

type ChatPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  analysisId: string;
};

export function ChatPanel({ isOpen, onClose, analysisId }: ChatPanelProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const assistant = useAssistantChat({ analysisId });
  const posthog = usePostHog();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [assistant.currentMessages, assistant.isTyping]);

  const handleSendPrompt = () => {
    if (!assistant.input.trim() || assistant.isTyping || !analysisId) return;

    posthog.capture("assistant_prompt_sent");
    void assistant.sendPrompt();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendPrompt();
    }
  };

  const handleSelectPrompt = (prompt: string) => {
    assistant.setInput(prompt);
    textareaRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-[360px] transition-transform duration-300">
      <aside className="h-full w-full bg-background border-l border-border flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <div
              className={`flex items-center gap-2 transition-all duration-300 ${assistant.streamingHasError ? "animate-pulse" : ""}`}
            >
              <MessageSquareIcon
                className={`w-4 h-4 transition-colors duration-300 ${assistant.streamingHasError ? "text-yellow-400 drop-shadow-[0_0_8px_rgb(250,204,21)]" : "text-violet-600"}`}
              />
              <span
                className={`font-semibold text-sm transition-colors duration-300 ${assistant.streamingHasError ? "text-yellow-400" : "text-foreground"}`}
              >
                Analysis Assistant
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-accent"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="border-b border-border flex-shrink-0">
          <div
            onClick={() =>
              assistant.setShowThreadList(!assistant.showThreadList)
            }
            className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 cursor-pointer"
          >
            <span>Threads</span>
            <div className="flex items-center gap-2">
              <span className="bg-violet-100 text-violet-700 text-xs px-1.5 py-0.5 rounded-full font-medium">
                {assistant.threads.length}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  assistant.createNewThread();
                }}
                className="flex items-center gap-1 text-violet-600 hover:text-violet-700 font-medium"
              >
                <PlusIcon className="w-3.5 h-3.5" /> <span>New</span>
              </button>
            </div>
          </div>
          <div
            className={`overflow-hidden transition-all ${assistant.showThreadList ? "max-h-48" : "max-h-0"}`}
          >
            {assistant.threads.map((thread) => (
              <div
                key={thread.id}
                className={`px-4 py-2.5 hover:bg-muted/50 border-t border-border/50 flex items-center gap-2 ${thread.id === assistant.activeThreadId ? "bg-violet-50" : ""}`}
              >
                {assistant.editingTitleId === thread.id ? (
                  <input
                    type="text"
                    value={assistant.tempTitle}
                    onChange={(e) => assistant.setTempTitle(e.target.value)}
                    onBlur={() => void assistant.saveTitle()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        void assistant.saveTitle();
                      } else if (e.key === "Escape") {
                        assistant.cancelEditing();
                      }
                    }}
                    className="flex-1 text-xs bg-background border border-violet-400 rounded px-2 py-1 focus:outline-none"
                    autoFocus
                  />
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        assistant.setActiveThreadId(thread.id);
                        assistant.setShowThreadList(false);
                      }}
                      className="flex-1 text-left"
                    >
                      <div className="flex items-center justify-between mb-0.5">
                        <span
                          className={`text-xs font-medium truncate flex items-center gap-1 ${thread.id === assistant.activeThreadId ? "text-violet-700" : ""}`}
                        >
                          {thread.name}
                          {assistant.justSavedId === thread.id && (
                            <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {thread.timestamp}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {thread.lastMessage}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        assistant.startEditingTitle(thread.id, thread.name)
                      }
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0">
          {assistant.currentMessages.length === 0 && !assistant.isTyping && (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <MessageSquareIcon className="w-8 h-8 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">No messages yet.</p>
              <p className="text-xs text-muted-foreground mt-1">
                Ask a question about this analysis to get started.
              </p>
            </div>
          )}
          {assistant.currentMessages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}
          {assistant.isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        <PromptLibrary
          activeThreadId={assistant.activeThreadId}
          hasMessages={assistant.hasMessages}
          loading={assistant.isLoadingSuggestions}
          onRefresh={() => void assistant.refreshSuggestions()}
          onSelectPrompt={handleSelectPrompt}
          suggestions={assistant.suggestions}
        />

        {assistant.error && (
          <div className="mx-3 mb-2 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-[10px] rounded-2xl flex items-center gap-2 transition-all duration-300 ease-in-out">
            <span className="flex-shrink-0 text-red-500">⚠</span>
            <span className="flex-1">{assistant.error}</span>
          </div>
        )}

        <div className="flex-shrink-0 px-3 pb-3 pt-2 border-t border-border">
          <div className="flex gap-2 items-end">
            <textarea
              ref={textareaRef}
              value={assistant.input}
              onChange={(e) => assistant.setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this analysis..."
              rows={2}
              className="flex-1 resize-none text-sm bg-muted/50 border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 transition-colors"
            />
            <button
              type="button"
              onClick={handleSendPrompt}
              disabled={
                !assistant.input.trim() || assistant.isTyping || !analysisId
              }
              className="flex-shrink-0 w-9 h-9 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              <SendIcon className="w-4 h-4 text-white" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5 px-1">
            Press Enter to send · Shift+Enter for new line
          </p>
        </div>
      </aside>
    </div>
  );
}
